import { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../firebase';
import type { MatchAlert } from '../types';

const REFRESH_INTERVAL_MS = 30_000;
const MAX_ALERTS = 20;

interface SmartAlertsState {
  alerts: MatchAlert[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  markViewed: (alertId: string) => Promise<void>;
  markAllViewed: () => Promise<void>;
  clearAlerts: () => Promise<void>;
}

export function useSmartAlerts(): SmartAlertsState {
  const [alerts, setAlerts] = useState<MatchAlert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchAlerts = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-match-alerts?limit=${MAX_ALERTS}`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (data.success) {
        setAlerts(data.alerts || []);
        setUnreadCount(data.unreadCount || 0);
      } else {
        throw new Error(data.error || 'Failed to fetch alerts');
      }
    } catch (err: any) {
      console.warn('[useSmartAlerts] fetch failed:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const markViewed = useCallback(async (alertId: string) => {
    const user = auth.currentUser;
    if (!user) return;

    // Optimistic update — keep alert visible but mark as viewed, decrement counter
    const wasNew = alerts.find(a => a.id === alertId)?.status === 'new';
    setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'viewed' } : a));
    if (wasNew) setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mark-alert-viewed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ alert_id: alertId }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.success && !data.alreadyViewed) {
        throw new Error(data.error || 'Failed to mark alert');
      }
    } catch (err: any) {
      console.warn('[useSmartAlerts] markViewed failed:', err.message);
      fetchAlerts();
    }
  }, [alerts, fetchAlerts]);

  const markAllViewed = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Optimistic update — keep alerts visible, set unread to 0
    setUnreadCount(0);
    setAlerts(prev => prev.map(a => ({ ...a, status: 'viewed' })));

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/mark-all-alerts-viewed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to mark all alerts');
      }
    } catch (err: any) {
      console.warn('[useSmartAlerts] markAllViewed failed:', err.message);
      fetchAlerts();
    }
  }, [fetchAlerts]);

  const clearAlerts = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Optimistic update — clear list and unread
    setAlerts([]);
    setUnreadCount(0);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/clear-my-alerts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to clear alerts');
      }
    } catch (err: any) {
      console.warn('[useSmartAlerts] clearAlerts failed:', err.message);
      fetchAlerts();
    }
  }, [fetchAlerts]);

  // Listen for smart-alerts:refresh events (fired after buyer/property creation)
  useEffect(() => {
    const handleRefresh = () => fetchAlerts();
    window.addEventListener('smart-alerts:refresh', handleRefresh);
    return () => window.removeEventListener('smart-alerts:refresh', handleRefresh);
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAlerts();
    timerRef.current = setInterval(fetchAlerts, REFRESH_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchAlerts]);

  return { alerts, unreadCount, loading, error, refresh, markViewed, markAllViewed, clearAlerts };
}
